import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationService } from '@memberjunction/ng-shared';
import { marked } from 'marked';
import { ChannelMessageEntityExtendedClient, IzzyMetadataEngine } from 'mj_generatedentities';
import { categorizeError, FriendlyError } from '../../utils/error-categorizer';

/** One rubric category parsed out of the evaluator's AIConfidenceReason text. */
interface CategoryBreakdown {
  name: string;
  score: number;
  maxScore: number;
  explanation: string;
  icon: string;
  percentage: number;
}

/**
 * AI Analysis pane — the confidence score, rubric breakdown, skip/error reasoning and edit diff
 * for a single ChannelMessage.
 *
 * Extracted from message-viewer so the Ticket Detail screen can show the identical analysis
 * (PRD: agents work the whole ticket without leaving it) instead of a second, drifting copy.
 * Deliberately message-scoped: AIConfidenceScore / AIConfidenceReason / ChannelID all live on
 * ChannelMessage, not on the Issue — so the HOST decides which message is being analysed.
 *
 * Used in:
 * - message-viewer (AI Analysis tab)
 * - ticket-detail (AI Analysis tab, with a message selector)
 */
@Component({
  standalone: false,
  selector: 'izzy-ai-analysis',
  templateUrl: './ai-analysis.component.html',
  // Shares message-viewer's stylesheet on purpose: this markup came from there and the two panes
  // must stay pixel-identical. Angular re-scopes the file per component, so there is one source of
  // truth for the analysis styling rather than a copy that drifts. Our own file comes second and
  // overrides the :host block, which is tuned for the Messages screen's full-height layout.
  styleUrls: ['../message-viewer/message-viewer.component.css', './ai-analysis.component.css'],
})
export class AIAnalysisComponent implements OnChanges, OnDestroy {
  /** The message whose analysis is shown. Null renders nothing (host shows its own empty state). */
  @Input() message: ChannelMessageEntityExtendedClient | null = null;
  /** True while a re-score is in flight — swaps the pane for the evaluating placeholder. */
  @Input() isRescoring: boolean = false;
  /** Whether this message is eligible for re-scoring (drives the button's visibility). */
  @Input() canRescore: boolean = false;
  /** True while the host is submitting the re-score request (disables the button). */
  @Input() isReprocessing: boolean = false;
  /** Owning organization ID — used for deep-linking to Credentials from error actions. */
  @Input() organizationId: string | null = null;

  /** Asks the host to re-run the confidence evaluator; the host owns the entity write. */
  @Output() rescoreRequested = new EventEmitter<void>();

  /** True briefly after a re-score completes, to stagger the category cards in. */
  protected animateCategories: boolean = false;
  private wasRescoring: boolean = false;
  private animationTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    private navigationService: NavigationService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Rescore just finished (isRescoring true → false): stagger the new category cards in.
    if (changes['isRescoring'] && this.wasRescoring && !this.isRescoring) {
      this.animateCategories = true;
      if (this.animationTimer) clearTimeout(this.animationTimer);
      // Clear the flag once animations finish (6 cards × 150ms stagger + 500ms duration).
      this.animationTimer = setTimeout(() => {
        this.animateCategories = false;
        this.cdr.markForCheck();
      }, 1500);
    }
    this.wasRescoring = this.isRescoring;

    if (changes['message']) {
      this.animateCategories = false;
    }
  }

  ngOnDestroy(): void {
    if (this.animationTimer) clearTimeout(this.animationTimer);
  }

  /** Pending = still awaiting a human decision; drives the "Needs Review" badge. */
  get isPending(): boolean {
    return this.message?.ApprovalStatus === 'Pending';
  }

  // ========== Category Breakdown Parsing ==========

  // Splits on either old format "Category N (X/Y):" or new format "Category Name (X/Y):"
  private readonly CATEGORY_SPLIT_RE = new RegExp(
    '(?=(?:Category\\s+\\d+|Factual\\s+Grounding|Question\\s+Completeness|' +
    'Safety\\s+(?:and|&)\\s+Risk|Tone\\s+(?:and|&)\\s+Professionalism|' +
    'Contextual\\s+Awareness|Clarity\\s+(?:and|&)\\s+Precision)\\s*\\(\\d+\\/\\d+\\):)',
    'i'
  );

  private matchCategoryDef(rawName: string): { name: string; icon: string } | null {
    const n = rawName.toLowerCase().replace(/\s+/g, ' ');
    if (n.includes('factual'))                                return { name: 'Factual Grounding',      icon: 'fa-magnifying-glass' };
    if (n.includes('question') || n.includes('completeness')) return { name: 'Question Completeness',  icon: 'fa-circle-check' };
    if (n.includes('safety'))                                 return { name: 'Safety & Risk',          icon: 'fa-shield-halved' };
    if (n.includes('tone'))                                   return { name: 'Tone & Professionalism', icon: 'fa-comments' };
    if (n.includes('context'))                                return { name: 'Contextual Awareness',   icon: 'fa-user' };
    if (n.includes('clarity') || n.includes('precision'))     return { name: 'Clarity & Precision',    icon: 'fa-bullseye' };
    // Legacy: numeric category fallback
    const legacyMap: Record<string, { name: string; icon: string }> = {
      '1': { name: 'Factual Grounding',      icon: 'fa-magnifying-glass' },
      '2': { name: 'Question Completeness',  icon: 'fa-circle-check' },
      '3': { name: 'Safety & Risk',          icon: 'fa-shield-halved' },
      '4': { name: 'Tone & Professionalism', icon: 'fa-comments' },
      '5': { name: 'Contextual Awareness',   icon: 'fa-user' },
      '6': { name: 'Clarity & Precision',    icon: 'fa-bullseye' },
    };
    const numMatch = n.match(/category\s+(\d+)/);
    return numMatch ? (legacyMap[numMatch[1]] ?? null) : null;
  }

  get parsedCategoryBreakdown(): CategoryBreakdown[] | null {
    const reason = this.message?.AIConfidenceReason;
    if (!reason) return null;

    const segments = reason.split(this.CATEGORY_SPLIT_RE).filter(s => s.trim());
    const results: CategoryBreakdown[] = [];

    for (const segment of segments) {
      const match = segment.match(/^\s*([\w\s&]+?)\s*\((\d+)\/(\d+)\):\s*([\s\S]*)/i);
      if (!match) continue;

      const catDef = this.matchCategoryDef(match[1].trim());
      const score = parseInt(match[2]);
      const maxScore = parseInt(match[3]);
      if (!catDef || isNaN(score) || isNaN(maxScore)) continue;

      const explanation = match[4]
        .replace(/\s*(?:Note:|Disqualifier:)[\s\S]*$/i, '')
        .replace(/\s*---[\s\S]*$/, '')
        .trim()
        .replace(/\.\s*$/, '');

      results.push({
        name: catDef.name,
        score,
        maxScore,
        explanation,
        icon: catDef.icon,
        percentage: maxScore > 0 ? Math.round((score / maxScore) * 100) : 0,
      });
    }

    return results.length >= 3 ? results : null;
  }

  get categoryBreakdownNote(): string | null {
    const reason = this.message?.AIConfidenceReason;
    if (!reason) return null;
    const match = reason.match(/\bNote:\s*([\s\S]*?)(?:---|$)/i);
    return match ? match[1].trim().replace(/\.\s*$/, '') : null;
  }

  get safetyNoticeText(): string | null {
    const reason = this.message?.AIConfidenceReason;
    if (!reason) return null;
    const match = reason.match(/\*\*Safety Notice:\*\*\s*([\s\S]*?)(?:\n\n|$)/);
    return match ? match[1].trim() : null;
  }

  getCategoryScoreClass(percentage: number): string {
    if (percentage >= 80) return 'confidence-high';
    if (percentage >= 60) return 'confidence-medium';
    return 'confidence-low';
  }

  getConfidenceScoreClass(score: number | null): string {
    if (score === null || score === undefined) return '';
    if (score >= 85) return 'confidence-high';
    if (score >= 60) return 'confidence-medium';
    return 'confidence-low';
  }

  /** Auto-approve cut-off for this message's channel, falling back to the platform default. */
  getAutoApproveThreshold(): number {
    if (this.message?.ChannelID) {
      const result = IzzyMetadataEngine.Instance.GetSettingValue('MinConfidenceForAutoApprove', this.message.ChannelID);
      if (result.value) {
        const parsed = parseInt(result.value, 10);
        if (!isNaN(parsed)) return parsed;
      }
    }
    return 90; // Default threshold
  }

  // ========== Error / Reasoning Rendering ==========

  /**
   * Builds the friendly error object for the currently-displayed message.
   * Returns null when there is no error to display.
   */
  getFriendlyError(): FriendlyError | null {
    if (!this.message?.ErrorType && !this.message?.ErrorMessage) return null;
    return categorizeError(this.message.ErrorType, this.message.ErrorMessage);
  }

  /**
   * Navigate to the Credentials admin section, filtered to AI category.
   * Used by the "Check AI credentials" deep-link on AI-related errors.
   */
  openAICredentials(): void {
    const params: Record<string, string> = { section: 'credentials', category: 'AI' };
    if (this.organizationId) params['organizationId'] = this.organizationId;
    this.navigationService.OpenNavItemByName('Administration', params);
  }

  renderMarkdown(content: string | null | undefined): SafeHtml {
    if (!content) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }
    // Replace &nbsp; with regular spaces to allow proper word wrapping — Quill inserts them.
    const normalizedContent = content.replace(/&nbsp;/g, ' ');
    let html = marked.parse(normalizedContent, { async: false }) as string;
    // Strip inline color and background styles so email content adapts to dark mode
    html = html.replace(/\b(color|background|background-color)\s*:\s*[^;"}]+;?/gi, '');
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
